import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { HttpTypes } from "@medusajs/types";
import type { SalonStockerHttpTypes } from "../types";

export class Product {
  /**
   * @ignore
   */
  private client: Client;
  /**
   * @ignore
   */
  constructor(client: Client) {
    this.client = client;
  }

  /**
   * This method retrieves a list of products. It sends a request to the
   * [List Products API route](https://docs.medusajs.com/api/store#products_getproducts).
   *
   * @param query - Filters and pagination configurations.
   * @param headers - Headers to pass in the request.
   * @returns The paginated list of products.
   */
  list = async (
    query?: HttpTypes.StoreProductListParams,
    headers?: ClientHeaders
  ): Promise<HttpTypes.StoreProductListResponse> => {
    return await this.client.fetch<HttpTypes.StoreProductListResponse>(
      `/store/products`,
      {
        method: "GET",
        query,
        headers
      }
    );
  };

  /**
   * This method is used to retrieve a product by its ID. It sends a request to the
   * [Get Product](https://docs.medusajs.com/api/store#products_getproductsid) API route.
   *
   * @param id - The product's ID.
   * @param query - Configure the fields to retrieve in the product.
   * @param headers - Headers to pass in the request
   * @returns The product.
   */
  retrieve = async (
    id: string,
    query?: HttpTypes.StoreProductParams,
    headers?: ClientHeaders
  ): Promise<HttpTypes.StoreProductResponse> => {
    return await this.client.fetch<HttpTypes.StoreProductResponse>(
      `/store/products/${id}`,
      {
        method: "GET",
        query,
        headers
      }
    );
  };

  // /**
  //  * This method retrieves a paginated list of featured products. It sends a request to the
  //  * `/store/products-featured` API route.
  //  *
  //  * @template TInclude - Union of optional field names to include in the response
  //  * @param query - Filters and pagination configurations.
  //  * @param headers - Headers to pass in the request.
  //  * @returns The paginated list of featured products.
  //  *
  //  * @example
  //  * To retrieve the list of featured products:
  //  *
  //  * ```ts
  //  * sdk.store.product.listFeatured()
  //  * .then(({ products, count, limit, offset }) => {
  //  *   console.log(products)
  //  * })
  //  * ```
  //  *
  //  * @example
  //  * To filter by brand or seller:
  //  *
  //  * ```ts
  //  * sdk.store.product.listFeatured({
  //  *   brand_id: "brand_123",
  //  *   seller_id: ["seller_1", "seller_2"]
  //  * })
  //  * .then(({ products, count, limit, offset }) => {
  //  *   console.log(products)
  //  * })
  //  * ```
  //  *
  //  * @example
  //  * To request optional fields (external_id):
  //  *
  //  * ```ts
  //  * sdk.store.product.listFeatured<'external_id'>({
  //  *   fields: "+external_id"
  //  * })
  //  * .then(({ products }) => {
  //  *   console.log(products[0].external_id) // Type-safe!
  //  * })
  //  * ```
  //  */
  // listFeatured = async <
  //   TInclude extends keyof StoreProductPreviewOptionalFields = never
  // >(
  //   query?: StoreFeaturedProductsParams,
  //   headers?: ClientHeaders
  // ): Promise<StoreProductFeaturedListPreviewResponse<TInclude>> => {
  //   return await this.client.fetch<
  //     StoreProductFeaturedListPreviewResponse<TInclude>
  //   >(`/store/products-featured`, {
  //     method: "GET",
  //     query,
  //     headers
  //   });
  // };

  /**
   * This method retrieves a paginated list of products currently on sale. It sends a request to the
   * `/store/products-sale` API route.
   *
   * @param query - Filters and pagination configurations.
   * @param headers - Headers to pass in the request.
   * @returns The paginated list of sale products.
   *
   * @example
   * To retrieve the list of sale products:
   *
   * ```ts
   * sdk.store.product.listSale()
   * .then(({ products, count, limit, offset }) => {
   *   console.log(products)
   * })
   * ```
   *
   * @example
   * To filter by category or collection:
   *
   * ```ts
   * sdk.store.product.listSale({
   *   category_id: "cat_123",
   *   collection_id: ["col_1", "col_2"]
   * })
   * .then(({ products, count, limit, offset }) => {
   *   console.log(products)
   * })
   */
  listSale = async (
    query?: SalonStockerHttpTypes.StoreProductSaleListParams,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreProductSaleListResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreProductSaleListResponse>(
      `/store/products-sale`,
      {
        method: "GET",
        query,
        headers
      }
    );
  };

  /**
   * This method retrieves accurate pricing information for a list of products.
   * It returns per-customer pricing based on seller agreements. It sends a request to the
   * `/store/products-pricing` API route.
   *
   * @param product_ids - Array of product IDs (max 15)
   * @param headers - Headers to pass in the request.
   * @returns Products with accurate pricing for all published variants.
   *
   * @example
   * To get accurate pricing for products:
   *
   * ```ts
   * sdk.store.product.resolvePricing(["prod_123", "prod_456"])
   * .then(({ products }) => {
   *   products.forEach(product => {
   *     console.log(product.title)
   *     product.variants.forEach(variant => {
   *       console.log(variant.seller.name, variant.calculated_price)
   *     })
   *   })
   * })
   */
  resolvePricing = async (
    product_ids: string[],
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreProductPricingResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreProductPricingResponse>(
      `/store/products-pricing`,
      {
        method: "POST",
        body: { product_ids },
        headers
      }
    );
  };
}
